import React from "react";
import styled, { css } from "styled-components";

/**
 * The pieces a wizard step is built from.
 *
 * Everything here is sized for a tablet on a shop counter: nothing is hidden
 * behind hover, and nothing is smaller than a thumb.
 */

export const TileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(148px, 1fr));
  gap: ${(p) => p.theme.spacing.sm};
`;

const TileButton = styled.button`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
  min-height: 68px;
  padding: 10px 12px;
  text-align: left;
  border: 1px solid ${(p) => p.theme.color.border};
  border-radius: ${(p) => p.theme.radius.md};
  background: ${(p) => p.theme.color.surface};
  font-family: ${(p) => p.theme.font.family};
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover:not(:disabled) {
    border-color: ${(p) => p.theme.color.primary};
  }

  &:disabled {
    opacity: 0.65;
  }

  ${(p) =>
    p.$auto &&
    css`
      border-style: dashed;
    `}

  ${(p) =>
    p.$filled === false &&
    css`
      border-color: ${p.theme.color.border};
    `}
`;

const TileLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 10px;
  font-weight: ${(p) => p.theme.font.weightSemibold};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${(p) => p.theme.color.textMuted};
`;

const TileValue = styled.span`
  font-size: 20px;
  font-weight: ${(p) => p.theme.font.weightSemibold};
  color: ${(p) => (p.$empty ? p.theme.color.textMuted : p.theme.color.text)};
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
  word-break: break-word;
`;

export const AutoPill = styled.span`
  padding: 1px 6px;
  border-radius: 999px;
  border: 1px solid ${(p) => p.theme.color.success};
  color: ${(p) => p.theme.color.success};
  font-size: 9px;
  letter-spacing: 0.06em;
`;

/** One value of the sheet: its name, what it says now, and a way in. */
export const FieldTile = ({
  label,
  value,
  auto = false,
  autoLabel = "auto",
  disabled = false,
  onClick,
}) => (
  <TileButton
    type="button"
    $auto={auto}
    $filled={!!value}
    disabled={disabled}
    onClick={onClick}
    title={label}
  >
    <TileLabel>
      {label}
      {auto && <AutoPill>{autoLabel}</AutoPill>}
    </TileLabel>
    <TileValue $empty={!value}>{value || "—"}</TileValue>
  </TileButton>
);

export const ChoiceRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${(p) => p.theme.spacing.sm};
`;

export const Choice = styled.button`
  min-height: 48px;
  min-width: 76px;
  padding: 8px 14px;
  border-radius: ${(p) => p.theme.radius.md};
  border: 1px solid
    ${(p) => (p.$on ? p.theme.color.primary : p.theme.color.border)};
  background: ${(p) => (p.$on ? p.theme.color.primary : p.theme.color.surface)};
  color: ${(p) => (p.$on ? "#ffffff" : p.theme.color.primary)};
  font-family: ${(p) => p.theme.font.family};
  font-size: ${(p) => p.theme.font.sizeMd};
  font-weight: ${(p) => p.theme.font.weightSemibold};
  font-variant-numeric: tabular-nums;

  &:hover:not(:disabled) {
    background: ${(p) =>
      p.$on ? p.theme.color.primaryDark : "rgba(0, 24, 126, 0.08)"};
  }

  &:disabled {
    opacity: 0.5;
  }
`;

/** A card for something the wizard worked out and is offering. */
export const SuggestionCard = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${(p) => p.theme.spacing.sm};
  padding: 12px 14px;
  border-radius: ${(p) => p.theme.radius.md};
  border: 1px solid ${(p) => p.theme.color.success};
  background: ${(p) => p.theme.color.successBg};
`;

export const SuggestionText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-family: ${(p) => p.theme.font.family};
  font-size: ${(p) => p.theme.font.sizeSm};
  color: ${(p) => p.theme.color.text};

  strong {
    font-size: ${(p) => p.theme.font.sizeLg};
    font-weight: ${(p) => p.theme.font.weightSemibold};
    color: ${(p) => p.theme.color.success};
    font-variant-numeric: tabular-nums;
  }
`;

export const ApplyButton = styled.button`
  min-height: 44px;
  padding: 8px 18px;
  border-radius: ${(p) => p.theme.radius.md};
  border: 1px solid ${(p) => p.theme.color.success};
  background: ${(p) => p.theme.color.success};
  color: #ffffff;
  font-family: ${(p) => p.theme.font.family};
  font-size: ${(p) => p.theme.font.sizeMd};
  font-weight: ${(p) => p.theme.font.weightSemibold};
  white-space: nowrap;

  &:disabled {
    opacity: 0.55;
  }
`;

export const CheckRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid ${(p) => p.theme.color.border};
  border-radius: ${(p) => p.theme.radius.md};
  background: ${(p) => p.theme.color.surface};
  font-family: ${(p) => p.theme.font.family};
  font-size: ${(p) => p.theme.font.sizeMd};
  color: ${(p) => p.theme.color.text};
  cursor: pointer;

  input {
    width: 20px;
    height: 20px;
    margin-top: 1px;
    flex: 0 0 auto;
    accent-color: ${(p) => p.theme.color.primary};
  }
`;
